import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { Tree } from "@/app/queries";
import { Button } from "@/ui/Button";
import { plural } from "@/lib/plural";
import { SLUG_RE, slugify, uniqueSlug, useCreateStep, useCreateTrack, useDeleteTrack, useMoveStep, useReorderTracks } from "./api";
import { INPUT } from "./shared";

type TreeTrack = Tree["tracks"][number];
type TreeStep = TreeTrack["steps"][number];

const STATE_FG: Record<string, string> = { готов: "var(--ok)", правится: "var(--acc)", черновик: "var(--mut)" };

/** Стрелка порядка: видна при наведении на строку (или всегда — у раскрытого трека). */
const ARROW = "cursor-pointer rounded-sm border-0 bg-transparent px-[3px] py-0.5 font-mono text-[11px] leading-none text-mut hover:bg-card2 hover:text-ink focus-visible:opacity-100";

/** Левая колонка A1: треки с точкой готовности и порядком, шаги с состоянием,
 *  вставка шага между шагами, перенос открытого шага, «+ шаг», «+ трек». */
export function StructureTree({ tree, activeId }: { tree: Tree; activeId?: string }) {
  const tracks = [...tree.tracks].sort((a, b) => a.position - b.position);
  const activeTrack = tracks.find((t) => t.steps.some((s) => s.id === activeId));
  const [open, setOpen] = useState<Record<string, boolean>>(() => (activeTrack ? { [activeTrack.id]: true } : {}));
  const reorder = useReorderTracks();
  const move = useMoveStep();
  const del = useDeleteTrack();
  const nTracks = tracks.length;

  const shiftTrack = (i: number, d: -1 | 1) => {
    const ids = tracks.map((t) => t.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    if (!reorder.isPending) reorder.mutate(ids);
  };
  // Пока идёт запрос, повторный клик игнорируется (кнопку не блокируем: без причины это баг).
  const moveStep = (stepId: string, trackId: string, position: number) => {
    if (move.isPending) return;
    move.mutate({ stepId, trackId, position }, { onSuccess: () => setOpen((o) => ({ ...o, [trackId]: true })) });
  };
  const error = reorder.error ?? move.error ?? del.error;

  return (
    <div className="flex min-h-0 flex-col border-r border-bd" data-testid="structure">
      <div className="flex flex-none items-baseline justify-between border-b border-bd px-3.5 py-[13px]">
        <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">структура</span>
        <span className="font-mono text-[11px] text-mut" data-testid="tree-summary">
          {nTracks} {plural(nTracks, "трек", "трека", "треков")} · {tree.steps_total} {plural(tree.steps_total, "шаг", "шага", "шагов")}
        </span>
      </div>
      <div className="flex-1 overflow-auto px-2.5 pb-4 pt-2.5">
        {error && (
          <div className="mb-2 rounded-sm px-2.5 py-2 font-mono text-[11px] text-err" style={{ background: "var(--errsoft)" }} role="alert">
            <span>{error.message}</span> <span>Структура не изменилась.</span>
          </div>
        )}
        {tracks.map((t, ti) => {
          const isOpen = !!open[t.id];
          const steps = [...t.steps].sort((a, b) => a.position - b.position);
          const arrowLook = isOpen ? "" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100";
          return (
            <div key={t.id} className="mb-1.5" data-testid="tree-track">
              <div className={`group grid grid-cols-[minmax(0,1fr)_auto] items-center rounded-[5px] ${isOpen ? "bg-card" : ""}`}>
                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [t.id]: !o[t.id] }))}
                  aria-expanded={isOpen}
                  className="grid w-full min-w-0 cursor-pointer grid-cols-[14px_minmax(0,1fr)_38px_14px] items-center gap-2 border-0 bg-transparent py-2 pl-[9px] pr-1 text-left font-sans text-ink"
                >
                  <span className="font-mono text-[10px] text-mut">{isOpen ? "▾" : "▸"}</span>
                  <span className={`truncate text-[12.5px] ${isOpen ? "text-ink" : "text-ink2"}`}>
                    {t.name}
                    {t.parallel ? " · параллельно" : ""}
                  </span>
                  <span className="text-right font-mono text-[10px] text-mut">{t.steps.length} ш.</span>
                  <span className="size-[7px] justify-self-end rounded-full" style={{ background: t.ready ? "var(--ok)" : "var(--warn)" }} title={t.ready ? "все шаги готовы" : "есть неготовые шаги"} />
                </button>
                <span className="flex w-[34px] justify-end pr-1">
                  {ti > 0 && (
                    <button type="button" aria-label={`поднять трек «${t.name}»`} title="выше в маршруте" onClick={() => shiftTrack(ti, -1)} className={`${ARROW} ${arrowLook}`}>
                      ↑
                    </button>
                  )}
                  {ti < nTracks - 1 && (
                    <button type="button" aria-label={`опустить трек «${t.name}»`} title="ниже в маршруте" onClick={() => shiftTrack(ti, 1)} className={`${ARROW} ${arrowLook}`}>
                      ↓
                    </button>
                  )}
                </span>
              </div>
              {isOpen && (
                <div className="flex flex-col py-1 pl-5">
                  {steps.map((s, si) => {
                    const active = s.id === activeId;
                    return (
                      <div key={s.id}>
                        <InsertSlot track={t} position={si + 1} before={s} />
                        <div
                          className="grid grid-cols-[minmax(0,1fr)_auto] items-center rounded-r-sm hover:bg-card2"
                          style={{ borderLeft: `2px solid ${active ? "var(--acc)" : "transparent"}`, background: active ? "var(--accsoft)" : undefined }}
                        >
                          <Link
                            to="/admin/course/$stepId"
                            params={{ stepId: s.id }}
                            aria-current={active ? "page" : undefined}
                            className="grid min-w-0 grid-cols-[minmax(0,1fr)_54px] items-center gap-2 px-[9px] py-[7px] no-underline"
                          >
                            <span className={`truncate text-[12px] ${active ? "text-ink" : "text-ink2"}`}>{s.title}</span>
                            {/* На подложке --accsoft статус --mut не проходит 4.5:1 — там --ink2. */}
                            <span className="text-right font-mono text-[10px]" style={{ color: active && s.state === "черновик" ? "var(--ink2)" : (STATE_FG[s.state] ?? "var(--mut)") }}>
                              {s.state}
                            </span>
                          </Link>
                          {active && (
                            <span className="flex pr-1">
                              {si > 0 && (
                                <button type="button" aria-label={`поднять шаг «${s.title}»`} title="на позицию выше" onClick={() => moveStep(s.id, t.id, si)} className={ARROW.replace("text-mut", "text-ink2")}>
                                  ↑
                                </button>
                              )}
                              {si < steps.length - 1 && (
                                <button type="button" aria-label={`опустить шаг «${s.title}»`} title="на позицию ниже" onClick={() => moveStep(s.id, t.id, si + 2)} className={ARROW.replace("text-mut", "text-ink2")}>
                                  ↓
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                        {active && <MoveToTrack step={s} track={t} tracks={tracks} onMove={(target) => moveStep(s.id, target.id, target.steps.length + 1)} />}
                      </div>
                    );
                  })}
                  <NewStep track={t} />
                  <DeleteTrack track={t} pending={del.isPending} onDelete={() => del.mutate(t.id)} />
                </div>
              )}
            </div>
          );
        })}
        <NewTrack tracks={tracks} onCreated={(id) => setOpen((o) => ({ ...o, [id]: true }))} />
      </div>
    </div>
  );
}

/** «удалить трек» с подтверждением в строке (без системного диалога). Трек с
 *  прогрессом студентов сервер не удалит — причина придёт в плашку ошибки. */
function DeleteTrack({ track, pending, onDelete }: { track: TreeTrack; pending: boolean; onDelete: () => void }) {
  const [ask, setAsk] = useState(false);
  const n = track.steps.length;
  if (!ask)
    return (
      <button type="button" onClick={() => setAsk(true)} className="mt-1 cursor-pointer self-start rounded-sm border-0 bg-transparent px-[9px] py-1 font-mono text-[11px] text-ink2 hover:bg-card2 hover:text-err">
        удалить трек
      </button>
    );
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 rounded-sm px-[9px] py-1.5 font-mono text-[11px] text-ink2" style={{ background: "var(--errsoft)" }} role="group" aria-label="подтверждение удаления трека">
      <span>
        Удалить «{track.name}»{n > 0 ? ` и ${n} ${plural(n, "шаг", "шага", "шагов")}` : ""}? Трек с прогрессом студентов не удалится.
      </span>
      <button type="button" onClick={() => { if (!pending) onDelete(); setAsk(false); }} className="cursor-pointer rounded-sm border border-err bg-transparent px-2 py-0.5 font-mono text-[11px] text-err">
        удалить
      </button>
      <button type="button" onClick={() => setAsk(false)} className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2 py-0.5 font-mono text-[11px] text-ink2">
        отмена
      </button>
    </div>
  );
}

/** Перенос открытого шага в другой трек — в конец выбранного трека. */
function MoveToTrack({ step, track, tracks, onMove }: { step: TreeStep; track: TreeTrack; tracks: TreeTrack[]; onMove: (t: TreeTrack) => void }) {
  const [target, setTarget] = useState("");
  const others = tracks.filter((t) => t.id !== track.id);
  if (others.length === 0) return null;
  const picked = others.find((t) => t.id === target);
  return (
    <div className="flex items-center gap-1.5 py-1 pl-[11px]" data-testid="move-step">
      <select
        aria-label="перенести шаг в трек"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        className="min-w-0 flex-1 rounded-sm border border-bd bg-bg2 px-1.5 py-[3px] font-mono text-[10.5px] text-ink2 outline-none focus:border-acc"
        title={`перенести «${step.title}» в конец другого трека`}
      >
        <option value="">в другой трек…</option>
        {others.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      {picked && (
        <button
          type="button"
          onClick={() => {
            onMove(picked);
            setTarget("");
          }}
          className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2 py-[3px] font-mono text-[10.5px] text-ink hover:border-acc hover:text-acc"
        >
          перенести
        </button>
      )}
    </div>
  );
}

/** Щель между шагами: при наведении — «+ шаг сюда», по клику — форма вставки. */
function InsertSlot({ track, position, before }: { track: TreeTrack; position: number; before: TreeStep }) {
  const [adding, setAdding] = useState(false);
  if (adding) return <StepForm track={track} position={position} onClose={() => setAdding(false)} />;
  return (
    <button
      type="button"
      aria-label={`вставить шаг перед «${before.title}»`}
      title={`вставить шаг на место ${position}`}
      onClick={() => setAdding(true)}
      className="group/slot flex h-[7px] w-full cursor-pointer items-center gap-1.5 border-0 bg-transparent px-[9px] py-0 opacity-0 transition-[height,opacity] hover:h-[18px] hover:opacity-100 focus-visible:h-[18px] focus-visible:opacity-100"
    >
      <span className="h-px flex-1 bg-bd2" />
      <span className="font-mono text-[10px] text-acc">+ шаг сюда</span>
      <span className="h-px flex-1 bg-bd2" />
    </button>
  );
}

function NewStep({ track }: { track: TreeTrack }) {
  const [adding, setAdding] = useState(false);
  if (adding) return <StepForm track={track} onClose={() => setAdding(false)} />;
  return (
    <button
      type="button"
      onClick={() => setAdding(true)}
      className="mt-1 cursor-pointer rounded-sm border border-dashed border-bd2 bg-transparent px-[9px] py-[7px] text-left font-mono text-[11px] text-mut hover:border-acc hover:text-acc"
    >
      + шаг
    </button>
  );
}

/** Форма нового шага: в конец трека или (position) на место со сдвигом соседей. */
function StepForm({ track, position, onClose }: { track: TreeTrack; position?: number; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const create = useCreateStep();
  const navigate = useNavigate();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    create.mutate(
      { trackId: track.id, title: t, position },
      {
        onSuccess: (doc) => {
          onClose();
          void navigate({ to: "/admin/course/$stepId", params: { stepId: doc.id } });
        },
      },
    );
  };
  return (
    <form onSubmit={submit} className="my-1 flex flex-col gap-1.5" data-testid="step-form">
      {position !== undefined && (
        <span className="font-mono text-[10.5px] text-mut">
          новый шаг {position} · следующие сдвинутся, отметки студентов останутся при своих шагах
        </span>
      )}
      <input
        autoFocus
        aria-label="название нового шага"
        placeholder="название шага"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className={`${INPUT} w-full font-sans text-[12px]`}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      />
      <div className="flex items-center gap-1.5">
        <Button type="submit" variant="primary" size="sm" disabled={!title.trim() || create.isPending} reason={create.isPending ? undefined : !title.trim() ? "нужно название" : undefined}>
          {create.isPending ? "создаём…" : "создать"}
        </Button>
        <button type="button" onClick={onClose} className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2.5 py-[5px] font-mono text-[11px] text-ink2">
          отмена
        </button>
      </div>
      {create.isError && <span className="font-mono text-[11px] text-err">{create.error.message}</span>}
    </form>
  );
}

/** «+ трек»: название, slug (из названия, пока не правили руками), «параллельно».
 *  Новый трек встаёт в конец маршрута; порядок — стрелками. */
function NewTrack({ tracks, onCreated }: { tracks: TreeTrack[]; onCreated: (id: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [parallel, setParallel] = useState(false);
  const create = useCreateTrack();
  const taken = tracks.map((t) => t.slug);

  const reset = () => {
    setAdding(false);
    setName("");
    setSlug("");
    setSlugTouched(false);
    setParallel(false);
    create.reset();
  };
  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="mt-2 w-full cursor-pointer rounded-sm border border-dashed border-bd2 bg-transparent px-[9px] py-[7px] text-left font-mono text-[11px] text-mut hover:border-acc hover:text-acc"
      >
        + трек
      </button>
    );
  }
  const owner = tracks.find((t) => t.slug === slug);
  const reason = !name.trim() ? "нужно название" : !SLUG_RE.test(slug) ? "slug: латиница, цифры и дефис" : owner ? `slug «${slug}» уже у трека «${owner.name}»` : undefined;
  const position = tracks.reduce((m, t) => Math.max(m, t.position), 0) + 1;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reason || create.isPending) return;
    create.mutate({ slug, name: name.trim(), position, parallel }, { onSuccess: (tr) => { onCreated(tr.id); reset(); } });
  };
  return (
    <form onSubmit={submit} className="mt-2 flex flex-col gap-2 rounded-md border border-bd bg-card p-2.5" data-testid="track-form">
      <span className="font-mono text-[10.5px] uppercase tracking-[.06em] text-mut">новый трек</span>
      <input
        autoFocus
        aria-label="название трека"
        placeholder="название трека"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (!slugTouched) setSlug(uniqueSlug(slugify(e.target.value), taken));
        }}
        onKeyDown={(e) => e.key === "Escape" && reset()}
        className={`${INPUT} w-full font-sans text-[12.5px]`}
      />
      <label className="flex items-center gap-1.5">
        <span className="flex-none font-mono text-[10.5px] text-mut">slug</span>
        <input
          aria-label="slug трека"
          placeholder="ml-system-design"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          className={`${INPUT} min-w-0 flex-1 py-[5px] font-mono text-[11px]`}
        />
      </label>
      <label className="flex cursor-pointer items-start gap-2">
        <input type="checkbox" checked={parallel} onChange={(e) => setParallel(e.target.checked)} className="mt-px size-[14px] flex-none cursor-pointer accent-[var(--acc)]" />
        <span className="text-[12px] leading-[1.4] text-ink2">
          параллельно
          <span className="block font-mono text-[10.5px] text-mut">идёт рядом с основной цепочкой и не блокирует её</span>
        </span>
      </label>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button type="submit" variant="primary" size="sm" aria-label="создать трек" disabled={!!reason || create.isPending} reason={reason}>
          {create.isPending ? "создаём…" : "создать"}
        </Button>
        <button type="button" onClick={reset} className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2.5 py-[5px] font-mono text-[11px] text-ink2">
          отмена
        </button>
      </div>
      {create.isError && (
        <span className="font-mono text-[11px] text-err" role="alert">
          {create.error.message}
        </span>
      )}
    </form>
  );
}
